import { useState, useContext } from "react";
import { useDispatch } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useReactFlow } from "reactflow";
import { TabContextApi } from "layouts/Flow Designer/context";
import { setSelectedNode, updateComponentPropsTab } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import StatusBadge from "cxfcomponents/StatusBadge";
import { ActiveVersionDialog } from "./ActiveDialog";
import { applyConnectorSchema, fetchConnectorSchema, getConnectorSelectedNode } from "helpers/connectorSchema";
import { toast } from "react-toastify";

const dateOptions = {
	year: "numeric",
	month: "long",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true
};

const Version = ({ version, activeVersion, selectedNode }) => {
	const [versionMenu, setVersionMenu] = useState(null);
	const reactFlowInstance = useReactFlow();
	const { getNode, setNodes } = reactFlowInstance;
	const { autoSaveFlow } = useContext(TabContextApi);
	const dispatch = useDispatch();
	const [openActiveVersionDialog, setOpenActiveVersionDialog] =
		useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	const handleSetVersionMenu = (event) => {
		if (event) {
			return setVersionMenu(event.currentTarget);
		}

		return setVersionMenu(null);
	};

	const handleUseVersion = () => {
		setOpenActiveVersionDialog(true);
		handleSetVersionMenu(null);
	};

	const handleCloseActiveVersionDialog = () => {
		setOpenActiveVersionDialog(false);
	};

	const handleChangeConnectorVersion = async () => {
		if (isUpdating) return;

		setIsUpdating(true);

		try {
			const schema = await fetchConnectorSchema(version.config_files);
			const node = getNode(selectedNode.elementId);

			if (!node) return;
			
			const updatedNode = applyConnectorSchema(node, schema, version.config_files, version.version, version.updated_datetime);
			setNodes((nodes) => nodes.map((current) => current.id === node.id ? updatedNode : current));
			dispatch(updateComponentPropsTab(`componentProps-${node.id}`, updatedNode.data.flowElement));
			dispatch(setSelectedNode({
				node: getConnectorSelectedNode(updatedNode),
				tabId: `componentProps-${node.id}`
			}));
			autoSaveFlow(reactFlowInstance);
			handleCloseActiveVersionDialog();
			handleSetVersionMenu(null);
		} catch (error) {
			toast.error(error.message);
		} finally {
			setIsUpdating(false);
		}
	};

	const renderStatus = () => {
		if (activeVersion?.version === version.version) {
			return <StatusBadge status="active" />;
		}

		return "";
	};

	const renderAction = () => {
		if (activeVersion?.version === version.version) {
			return false;
		}

		return true;
	};

	return (
		<>
			<SoftBox
				sx={{
					alignItems: "flex-start",
					display: "flex",
					padding: "16px 24px 0 4px"
				}}
			>
				<SoftBox
					sx={{
						paddingRight: "20px",
						width: "100%"
					}}
				>
					<SoftBox
						sx={{
							display: "flex",
							justifyContent: "space-between",
							marginBottom: "8px",
							width: "100%"
						}}
					>
						<SoftBox
							sx={{ display: "flex", alignItems: "center", gap: "8px" }}
						>
							{renderStatus()}
							<SoftTypography sx={{ fontSize: "14px", fontWeight: 500 }}>
								Version#{" "}
								<span style={{ fontWeight: 700 }}>
									{version.version}{" "}
								</span>
							</SoftTypography>
						</SoftBox>
						<SoftTypography sx={{ fontSize: "14px", fontWeight: 500 }}>
							{new Date(version.created_datetime).toLocaleString(
								"en-US",
								dateOptions
							)}
						</SoftTypography>
					</SoftBox>
				</SoftBox>
				<Tooltip
					title="More Actions"
					placement="top"
				>
					<Icon
						fontSize="default"
						onClick={handleSetVersionMenu}
						sx={{
							cursor: "pointer",
							visibility: renderAction() ? "visible" : "hidden"
						}}
					>
						{"more_vertical_icon"}
					</Icon>
				</Tooltip>
				<Menu
					anchorEl={versionMenu}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					open={Boolean(versionMenu)}
					onClose={() => handleSetVersionMenu(null)}
					keepMounted
				>
					<MenuItem onClick={handleUseVersion}>Use Version</MenuItem>
				</Menu>
			</SoftBox>
			<ActiveVersionDialog
				open={openActiveVersionDialog}
				onClose={handleCloseActiveVersionDialog}
				onActive={handleChangeConnectorVersion}
			/>
		</>
	);
};

export default Version;
